import { useEffect, useState } from 'react'
import {
  NativeSyntheticEvent,
  TextInputChangeEventData,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import { ModalBottom } from '@ui/components/Modal'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import CodeScannerInput from '@/common/CodeScanner/CodeScannerInput'
import { useQrScanner } from '@/common/CodeScanner/useQrScanner'
import { trpc } from 'api'
import { trpcProxyClient } from 'api/ApiProvider'
import { useToast } from '@ui/toasts/useToastManager'
import { colors } from '@ui/colors'
import usePayments from '@order/hooks/usePayments'
import { hasPaymentMetadataOrders } from '@ca/common'
import useOrderSubmit from '@order/hooks/useOrderSubmit'

interface Props {
  open: boolean
  close: () => void
  numericOrderIds: number[]
  localId: string
}

export default function CheckRefundBalanceModal({
  open,
  close,
  numericOrderIds,
  localId,
}: Props) {
  const { t } = useTranslation(['modals', 'auth'])
  const text = 'modals:checkRefundBalance'

  const [staffId, setStaffId] = useState('')

  const setQrMode = useQrScanner((s) => s.setMode)

  useEffect(() => {
    if (!open) return
    setQrMode('dynamic')
    return () => {
      setQrMode('disabled')
    }
  }, [open])

  const toast = useToast()

  const cachedMetadata = usePayments((s) => s.payments[localId]?.metadata ?? null)
  const setMetadata = usePayments((s) => s.setMetadata)

  const fetchMetadata = async () => {
    try {
      const result = await trpcProxyClient.order.payments.metadata.query({
        order_ids: numericOrderIds,
      })
      if (!hasPaymentMetadataOrders(result)) {
        toast({ variant: 'error', text: t(`${text}.toast.submitError`) })
        handleClose()
        return
      }
      setMetadata(localId, result)
      toast({ variant: 'success', text: t(`${text}.toast.balanceFound`) })
      handleClose()
    } catch {
      toast({ variant: 'error', text: t(`${text}.toast.submitError`) })
      handleClose()
    }
  }

  const submitOrder = useOrderSubmit({
    onSuccess: fetchMetadata,
    onError: () => {
      toast({ variant: 'error', text: t(`${text}.toast.submitError`) })
      handleClose()
    },
  })

  const staffVerify = trpc.staff.staffLogin.useMutation({
    onSuccess: async () => {
      if (cachedMetadata) {
        toast({ variant: 'success', text: t(`${text}.toast.balanceFound`) })
        handleClose()
        return
      }
      submitOrder.mutate(numericOrderIds)
    },
    onError: () => {
      toast({ variant: 'error', text: t('auth:staff.error.invalid') })
    },
  })

  const isPending = staffVerify.isPending || submitOrder.isPending

  const handleContinue = () => {
    if (!staffId.trim() || isPending) return
    staffVerify.mutate({ ref: staffId.trim() })
  }

  const handleClose = () => {
    setStaffId('')
    staffVerify.reset()
    submitOrder.reset()
    close()
  }

  const handleUserClose = () => {
    if (isPending) return
    handleClose()
  }

  return (
    <ModalBottom
      hideBottomSpace
      open={open}
      close={handleUserClose}
      title={t(`${text}.title`)}
      desc={t(`${text}.desc`)}
      spacedText
      body={
        <View className="flex flex-col gap-12 px-16 pb-16">
          <Input
            label={t('auth:staffId.label')}
            value={staffId}
            onChange={(e: NativeSyntheticEvent<TextInputChangeEventData>) =>
              setStaffId(e.nativeEvent.text)
            }
            action={<CodeScannerInput onSubmit={setStaffId} />}
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={handleContinue}
            enterKeyHint="done"
            keyboardAppearance="dark"
          />

          <View className="flex flex-row items-center gap-8">
            <Button
              variant="filled"
              label={t(`${text}.cta.continue`)}
              onPress={handleContinue}
              loading={isPending}
              disabled={!staffId.trim()}
              colors={[colors.red[600], colors.red[700]]}
            />
            <Button
              variant="outlined"
              label={t(`${text}.cta.cancel`)}
              onPress={handleUserClose}
              disabled={isPending}
            />
          </View>
        </View>
      }
    />
  )
}
